/**
 * RuleConditionsQueryBuilder — controlled, Formik-agnostic, scope-agnostic.
 *
 * Renders react-querybuilder against a pre-fetched field registry, with all
 * controlElements replaced by CMS Core wrappers (DR §5.12). Every RQB control
 * reads its cross-cutting state (e.g. "should we show Required errors") from
 * QueryBuilderContext rather than reaching into Formik directly.
 *
 * The component knows nothing about LOB / sub-orgs / entity / the assignments
 * endpoint — callers fetch the registry (via `useConditionFieldRegistry` for
 * assignments callers, or anything else for future consumers) and decide
 * whether the QB should render at all by passing `prerequisiteMessage`. This
 * keeps the pure component reusable across rule-authoring surfaces with
 * different scoping concerns.
 *
 * Use `RuleConditionsQueryBuilderFormik` for the Formik-bound flavour.
 */

import React, { useCallback, useMemo } from 'react';
import type { Classnames, Controls, Field, FullField, RuleGroupType, RuleType } from 'react-querybuilder';
import { defaultPlaceholderFieldName, prepareRuleGroup, QueryBuilder } from 'react-querybuilder';

import { isMatchRow, MATCH_ROW_PLACEMENT, quantifierProps, SUB_QUERY_BUILDER_PROPS } from './collectionSubQuery';
import { QueryBuilderContext } from './context';
import {
  AddGroupAction,
  AddRuleAction,
  CombinatorSelector,
  FieldSelector,
  MatchModeSelector,
  OperatorSelector,
  RemoveGroupAction,
  RemoveRuleAction,
} from './controls';
import { fieldOffersOffset } from './dateRelations';
import { operatorsForField } from './operators';
import type { FieldDefinitionDto } from './types';
import { FIELD_TYPE, VARIANT_HINT_KEY } from './types';
import ValueEditor from './ValueEditor';

const EMPTY_QUERY: RuleGroupType = { combinator: 'and', rules: [] };

const CONTROL_ELEMENTS: Partial<Controls<FullField, string>> = {
  fieldSelector: FieldSelector,
  operatorSelector: OperatorSelector,
  combinatorSelector: CombinatorSelector,
  matchModeEditor: MatchModeSelector,
  valueEditor: ValueEditor,
  addRuleAction: AddRuleAction,
  addGroupAction: AddGroupAction,
  removeRuleAction: RemoveRuleAction,
  removeGroupAction: RemoveGroupAction,
};

// Tailwind classes applied to RQB's structural elements (we intentionally do
// NOT import react-querybuilder/dist/query-builder.css). Project's slate scale
// is overridden in src/theme/tailwind/colors.ts: slate-200=#F5F5F5,
// slate-500=#E0E0E0, slate-800=#606060, slate-900=#202020.
const CONTROL_CLASSNAMES: Partial<Classnames> = {
  // Conditions card — slate-200 fill + slate-500 border, matches the legacy
  // <Conditions> wrapper used in Communications/Exposures. Nested groups (RQB
  // sets data-level on every group; root=0, nested=1+) keep the same fill but
  // get a darker border so they read as a contained sub-group; the visible
  // border alone communicates nesting, no extra indent needed.
  // NOTE: this project overrides Tailwind's spacing scale to raw pixel values
  // (see src/theme/tailwind/spacing.ts), so e.g. p-20 = 20px, gap-24 = 24px,
  // not the default rem-based scale.
  ruleGroup: [
    'flex flex-col gap-24 p-20 bg-slate-200 border border-solid border-slate-500 rounded',
    '[&:not([data-level="0"])]:border-slate-800',
  ].join(' '),
  header: 'flex flex-wrap items-center gap-12',
  // `[&:empty]:hidden` collapses the body (and the parent's gap-24) when the
  // group has no rules, so an empty card hugs its header instead of leaving a
  // ~50px gap below the Add buttons.
  body: 'flex flex-col gap-24 [&:empty]:hidden',
  // 12-portion grid: Field=4, Operator=2, Value=6, plus a fixed 32px trash
  // column. Mins keep cells readable when the dialog narrows.
  rule: 'grid grid-cols-[minmax(200px,4fr)_minmax(120px,2fr)_minmax(200px,6fr)_32px] gap-16 items-start',
  // Inner CMS Core fields stretch to fill their grid cell.
  fields: '[&>*]:w-full',
  operators: '[&>*]:w-full',
  value: '[&>*]:w-full',
  // Pin the trash to the last column and bottom-align so the icon sits at the
  // input-underline level instead of the cell's vertical centre. col-start-4
  // also guards against RQB occasionally rendering only 3 of 4 children (e.g.
  // when the operator is unset and the ValueEditor is suppressed) — without
  // it, the trash would slide into column 3 and look misplaced.
  removeRule: 'col-start-4 self-end justify-self-center',
};

// A match row is one ordinary condition line and then two full-width blocks — see
// `MATCH_ROW_PLACEMENT`, which every builder's match row shares. The root's own column tracks:
// `rule`'s field track, then the mode cell and a spacer, with the trash in the column every
// sibling row uses.
const MATCH_RULE_CLASS = `!grid-cols-[minmax(200px,4fr)_minmax(160px,3fr)_1fr_32px] ${MATCH_ROW_PLACEMENT}`;

export interface RuleConditionsQueryBuilderProps {
  /** Controlled value — pass `null` for "no rules yet". */
  value: RuleGroupType | null;
  /** Called with the new tree on every change. */
  onChange: (q: RuleGroupType) => void;
  /** When true, controls render their "Required" red state for empty selections.
   *  The Formik wrapper sets this to `submitCount > 0`. */
  shouldShowErrors: boolean;
  /** Field registry — caller fetches it (e.g. via `useConditionFieldRegistry`)
   *  and passes it in so the parent form can validate against the same list. */
  fields: FieldDefinitionDto[];
  isLoading: boolean;
  /** When non-null, render this string in place of the QueryBuilder — used to
   *  prompt the user for missing scope ("Select a Line of Business…"). The
   *  caller owns the wording because the prerequisites differ per surface
   *  (assignments needs LOB + sub-org; a hypothetical workflow-engine surface
   *  might need different scope). `null` = prerequisites met, render the QB. */
  prerequisiteMessage: string | null;
  /** Copy shown when prerequisites are met but the registry returns zero fields.
   *  Defaults to a generic message; assignments-specific callers can override. */
  emptyMessage?: string;
  /** Rule paths the author may not edit or remove, in RQB's `disabled` form. Used where a leaf is
   *  part of the rule's definition rather than something the author chose — the AI-agent condition
   *  a claim-action guardrail is seeded with. RQB disables every control on the path, the remove
   *  button included. Omit for a fully editable tree. */
  disabledPaths?: number[][];
  /** Whether the rule has a triggering event, which is what makes "When the event occurred" a
   *  target a date leaf can pick. Off by default: a rule set with no event (assignments, automatic
   *  exposures, delegated authority, policy categories) has no event time to evaluate it against. */
  hasTriggeringEvent?: boolean;
}

const RuleConditionsQueryBuilder: React.FC<RuleConditionsQueryBuilderProps> = ({
  value,
  onChange,
  shouldShowErrors,
  fields,
  isLoading,
  prerequisiteMessage,
  emptyMessage = 'No condition fields available.',
  disabledPaths,
  hasTriggeringEvent = false,
}) => {
  // Every registry entry reaches the picker, collection fields included. A collection field has
  // no scalar operand space — `OPERATORS_BY_TYPE.collection` is empty — and RQB never consults
  // that list for one: a field declaring `matchModes` gets a match-mode dropdown where the
  // operator cell would be, and a nested builder over its `subproperties` where the value editor
  // would be. Both are derived from the `collection` hint block the provider publishes — the
  // subproperties from the registry too, where the block names a root the registry carries — so a
  // second collection field from any provider is authorable with no change here.
  const rqbFields: Field[] = useMemo(() => {
    return fields.map((f) => {
      return {
        name: f.path,
        label: f.label,
        operators: operatorsForField(f.field_type, Boolean(f.hints?.[VARIANT_HINT_KEY])),
        // `matchModes` and `subproperties` — declared the same way for a collection nested as an
        // element of another, which is what lets a body hold a match leaf of its own.
        ...quantifierProps(f, fields),
        // Custom props — RQB passes them through as `fieldData`.
        field_type: f.field_type,
        enum_values: f.enum_values,
        hints: f.hints,
        group: f.group,
        source: f.source,
      } as Field & FieldDefinitionDto;
    });
  }, [fields]);

  // Stable by construction — the same chrome for every collection field, since the *fields* a
  // sub-query offers come from that field's `subproperties` and not from here. RQB re-derives its
  // whole configuration when this object's identity changes.
  const getSubQueryBuilderProps = useCallback(() => SUB_QUERY_BUILDER_PROPS, []);

  // A match leaf renders five children where an ordinary leaf renders four — the mode editor
  // replaces two cells and the sub-query contributes a header and a body — so the two rows cannot
  // share one column template. RQB resolves this per rule, and hands the hook the row's
  // `fieldData` alongside it; `isMatchRow` says why that, and not `rule.match`, is the key.
  const getRuleClassname = useCallback(
    (_rule: RuleType, { fieldData }: { fieldData: FullField }) => (isMatchRow(fieldData) ? MATCH_RULE_CLASS : ''),
    []
  );

  // The target picker on a date leaf measures one date against another, so it
  // offers the registry's date entries only. Through `fieldOffersOffset` rather than
  // a second copy of the predicate: that function is what the operator cell and the
  // value cell already ask, and it is the one the BE↔FE contract test parses, so a
  // local copy here would drift out from under both without failing anything.
  const dateFields = useMemo(
    () => fields.filter((f) => fieldOffersOffset(f.field_type, Boolean(f.hints?.[VARIANT_HINT_KEY]))),
    [fields]
  );

  // The "compared to" picker on a user leaf: the registry's other user entries. Type-guarded ones
  // are left out for the reason they are left out of `dateFields` — the compiler refuses one as
  // an anchor.
  const userFields = useMemo(
    () => fields.filter((f) => f.field_type === FIELD_TYPE.User && !f.hints?.[VARIANT_HINT_KEY]),
    [fields]
  );

  // Prepared, not passed through raw. react-querybuilder is controlled by this prop and takes a
  // tree whose root carries an `id` to mean "already prepared": given one without, its
  // `resolveCandidateQuery` re-prepares on *every* render, minting a fresh `id` for the root and
  // every rule each time. Those ids are the React keys RQB renders its rows under, so an
  // unprepared tree remounts every cell on every render — which resets the ref-guard in
  // `useAutoSelectSingleOption` and lets a single-option field re-fire its auto-fill forever
  // (React stops it as an update-depth overflow). Stored `conditions_json` never carries ids, so
  // this is the shape every caller actually arrives with. Memoised on `value` so the ids stay
  // put across renders; a tree that already has them is returned as-is, which is what makes
  // echoing RQB's own `onQueryChange` output back in a no-op.
  const query: RuleGroupType = useMemo(() => prepareRuleGroup(value ?? EMPTY_QUERY), [value]);

  // One answer per leaf to "can this be rendered at all", from the same registry RQB is given,
  // so the field / operator / value cells cannot disagree. RQB's placeholder is excluded: a row
  // the author just added carries it and is not yet meant to resolve.
  const knownPaths = useMemo(() => new Set(fields.map((f) => f.path)), [fields]);
  const isUnknownFieldPath = useCallback(
    (path: string | undefined) => !!path && path !== defaultPlaceholderFieldName && !knownPaths.has(path),
    [knownPaths]
  );

  const ctxValue = useMemo(
    () => ({ shouldShowErrors, dateFields, userFields, hasTriggeringEvent, isUnknownFieldPath }),
    [shouldShowErrors, dateFields, userFields, hasTriggeringEvent, isUnknownFieldPath]
  );

  const placeholderClass = 'p-20 bg-slate-200 border border-solid border-slate-500 rounded text-sm text-slate-800';
  // The dialog wraps each form row in a MUI <Grid item> with 3px L/R padding
  // (from `spacing={1}` on the container). Pulling the card out by -3px on
  // each side lets it sit flush with the form-row content area now that it
  // has a visible border, while leaving the "Conditions" heading aligned with
  // the form labels above.
  const cardOutsetClass = '-mx-[3px]';

  const prerequisitesMet = prerequisiteMessage === null;

  return (
    <div className="flex flex-col gap-2">
      <span className="font-bold text-slate-900">Conditions</span>
      {!prerequisitesMet && (
        <div className={`${placeholderClass} ${cardOutsetClass} opacity-60`}>{prerequisiteMessage}</div>
      )}
      {prerequisitesMet && isLoading && <div className={`${placeholderClass} ${cardOutsetClass}`}>Loading fields…</div>}
      {prerequisitesMet && !isLoading && fields.length === 0 && (
        <div className={`${placeholderClass} ${cardOutsetClass}`}>{emptyMessage}</div>
      )}
      {prerequisitesMet && !isLoading && fields.length > 0 && (
        <QueryBuilderContext.Provider value={ctxValue}>
          <div className={cardOutsetClass}>
            <QueryBuilder
              fields={rqbFields}
              query={query}
              onQueryChange={onChange}
              controlElements={CONTROL_ELEMENTS}
              controlClassnames={CONTROL_CLASSNAMES}
              // `undefined` leaves the tree fully editable; an empty array would too, but the
              // explicit undefined keeps RQB on its own default path.
              disabled={disabledPaths}
              // The nested builder inherits nothing from this one. Without this it renders RQB's
              // stock native selects and buttons inside a dialog built entirely from CMS Core.
              getSubQueryBuilderProps={getSubQueryBuilderProps}
              getRuleClassname={getRuleClassname}
              autoSelectField={false}
              autoSelectOperator={false}
              // Already RQB's default, but pinned because the relation control depends on
              // it: a relation carries the value it just computed into the same update as
              // the operator, and a reset would immediately discard it.
              resetOnOperatorChange={false}
              listsAsArrays
            />
          </div>
        </QueryBuilderContext.Provider>
      )}
    </div>
  );
};

export default RuleConditionsQueryBuilder;
